import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { emptyProject, emptyProposal } from "./data/defaults";
import { ProposalDocument } from "./document/ProposalDocument";
import { ArtworksEditor, BrandOffsetControl } from "./editor/ArtworksEditor";
import { ProjectEditor } from "./editor/ProjectEditor";
import { COVER_BRAND_OFFSET_MAX, COVER_BRAND_OFFSET_MIN } from "./document/pages/CoverPage";
import { releaseImage } from "./editor/images";
import { PriceEditor } from "./editor/PriceEditor";
import { InvoicePanel, LinkedPriceSummary, QuotePanel } from "./InvoicePanel";
import { approveProposal, convertQuote, createProposal, fetchProposal, toPayload, updateProposal } from "./proposalApi";
import "./styles/fonts.css";
import "./styles/document.css";
import "./styles/editor.css";

// فاتورة المشروع وتسعيرته محصورة ببنين والمدير (PROJECT_INVOICE_USERS بـInvoicePage.jsx)
const PROJECT_INVOICE_USERS = ["admin", "Baneen@123"];

/** 1125pt page = 1500 CSS px. */
const PAGE_PX = 1500;

// active: the preview only exists once the proposal has loaded
function usePageScale(ref, active) {
    const [scale, setScale] = useState(1);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const measure = () => {
            const styles = getComputedStyle(el);
            const inner = el.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
            setScale(Math.min(1, inner / PAGE_PX));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref, active]);
    return scale;
}

// حجم صفحة الطباعة (1125 × 810 pt) يتفعّل بس طول ما المحرر مفتوح - حتى ما
// يغيّر طباعة باقي صفحات Artika (الفواتير...)
function useProposalPrintMode() {
    useEffect(() => {
        const style = document.createElement("style");
        style.textContent = "@page { size: 1125pt 810pt; margin: 0; }";
        document.head.appendChild(style);
        document.body.classList.add("proposal-print-mode");
        return () => {
            style.remove();
            document.body.classList.remove("proposal-print-mode");
        };
    }, []);
}

const snapshot = (data) => JSON.stringify(toPayload(data));

// رسائل الباك اند (proposalController.js's approveProposal) بالعربي
const APPROVE_ERRORS = {
    "Not the latest invoice version": "انحفظت نسخة أحدث من الفاتورة. افتح الفاتورة واحفظها حتى يتحدّث العرض، وبعدين اعتمده.",
    "Invoice is already approved": "الفاتورة معتمدة مسبقاً بنسخة ثانية.",
    "Order already created": "الفاتورة تحوّلت لطلب مسبقاً.",
    "Invoice not found": "الفاتورة المرتبطة بالعرض انحذفت.",
};
const approveErrorMessage = (message) => `تعذّر الاعتماد: ${APPROVE_ERRORS[message] || message}`;

/** /proposals/new = عرض جديد، /proposals/:id = تعديل عرض محفوظ */
export default function ProposalEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(emptyProposal);
    const [saved, setSaved] = useState(() => snapshot(data));
    const [loading, setLoading] = useState(Boolean(id));
    const [loadError, setLoadError] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploads, setUploads] = useState(0);
    const [previewOnly, setPreviewOnly] = useState(false);
    // 📐 عرض مشروع: { stage, quoteNumber, invoiceLink, invoiceStatus } من الباك اند
    // (تسعيرة/الزبون رفض/مرتبط بفاتورة) - null = عرض يدوي
    const [link, setLink] = useState(null);
    const [stageBusy, setStageBusy] = useState(false);
    const canManageProject = PROJECT_INVOICE_USERS.includes(localStorage.getItem("username"));
    // إعدادات التصميم (الخلفيات/مكان الشعار) للمدير بس
    const isAdmin = localStorage.getItem("username") === "admin";
    const [approving, setApproving] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const previewRef = useRef(null);
    // بعد أول حفظ ننتقل لـ/proposals/:id - البيانات أصلاً بالذاكرة فما نعيد جلبها
    const justCreatedId = useRef(null);
    useProposalPrintMode();

    const dirty = snapshot(data) !== saved;
    const scale = usePageScale(previewRef, !loading && !loadError);

    useEffect(() => {
        if (!id || id === justCreatedId.current)
            return;
        let alive = true;
        setLoading(true);
        setLoadError("");
        fetchProposal(id)
            .then((doc) => {
            if (!alive)
                return;
            const next = { recipientName: doc.recipientName || "", coverBrandOffset: Number(doc.coverBrandOffset) || 0, artworks: doc.artworks || [], price: { ...emptyProposal().price, ...doc.price }, project: { ...emptyProject(), ...doc.project } };
            setData(next);
            setSaved(snapshot(next));
            setLink(doc.invoiceLink || doc.stage
                ? { stage: doc.stage, quoteNumber: doc.quoteNumber, invoiceLink: doc.invoiceLink, invoiceStatus: doc.invoiceStatus, invoiceData: doc.invoiceData }
                : null);
        })
            .catch((err) => alive && setLoadError(err.message))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [id, reloadKey]);

    useEffect(() => {
        if (!dirty)
            return;
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);

    const trackUpload = async (promise) => {
        setUploads((n) => n + 1);
        try {
            return await promise;
        }
        finally {
            setUploads((n) => n - 1);
        }
    };

    // يرجّع true إذا انحفظ (يستخدمه الاعتماد قبل ما يكمل)
    const save = async () => {
        if (uploads > 0) {
            window.alert("انتظر لحين اكتمال رفع الصور ثم احفظ.");
            return false;
        }
        setSaving(true);
        try {
            if (id) {
                await updateProposal(id, data);
            }
            else {
                const res = await createProposal(data);
                justCreatedId.current = res.id;
                navigate(`/proposals/${res.id}`, { replace: true });
            }
            setSaved(snapshot(data));
            return true;
        }
        catch (err) {
            window.alert(`تعذّر حفظ العرض: ${err.message}`);
            return false;
        }
        finally {
            setSaving(false);
        }
    };

    const leaveTo = (path) => {
        if (dirty && !window.confirm("في تعديلات غير محفوظة. تريد تطلع بدون حفظ؟"))
            return;
        navigate(path);
    };
    const back = () => leaveTo("/proposals");

    // 📋 "اعتماد التسعيرة": يحفظ تعديلات العرض أولاً، ونفس الفاتورة تتحوّل
    // لفاتورة مشروع (بدون قيود كلفة) - تنفتح حتى تكمل تفاصيل اللوحات
    const approveQuote = async () => {
        if (!window.confirm(`راح تنعتمد التسعيرة رقم ${link.invoiceLink.invoiceNumber} وتتحوّل لفاتورة مشروع.\nبعدها تكملين تفاصيل كل لوحة (الشكل، الإطار، الصور) وتحفظين. متأكدة؟`))
            return;
        if (dirty && !(await save()))
            return;
        setStageBusy(true);
        try {
            const { invoiceId } = await convertQuote(id);
            navigate(`/invoice/edit/${invoiceId}`);
        }
        catch (err) {
            window.alert(`تعذّر اعتماد التسعيرة: ${err.message}`);
            setReloadKey((k) => k + 1);
        }
        finally {
            setStageBusy(false);
        }
    };

    // 📐 "اعتماد وتحويل لطلب": يحفظ تعديلات العرض أولاً، يعتمد نسخة الفاتورة
    // المرتبطة بالباك اند (نفس اعتماد الفواتير)، وينتقل لإضافة الطلب بنفس
    // البيانات اللي جانت تنبعث من صفحة الفاتورة
    const approve = async () => {
        const status = link?.invoiceStatus || {};
        const question = status.approved
            ? "الفاتورة معتمدة. تريد تكمل وتحوّلها لطلب؟"
            : `راح تنعتمد الفاتورة رقم ${link.invoiceLink.invoiceNumber} وتنتقل لإضافة الطلب.
بعد الاعتماد ما تكدر تعدّل الفاتورة. متأكد؟`;
        if (!window.confirm(question))
            return;
        if (dirty && !(await save()))
            return;
        setApproving(true);
        try {
            const { orderHandoff } = await approveProposal(id);
            navigate("/add", { state: orderHandoff });
        }
        catch (err) {
            window.alert(approveErrorMessage(err.message));
            setReloadKey((k) => k + 1);
        }
        finally {
            setApproving(false);
        }
    };

    const releaseAll = (d) => {
        d.artworks.forEach((p) => p.slots.forEach((s) => releaseImage(s.image)));
        [d.project?.photo, d.project?.arrangement].forEach((s) => s && releaseImage(s.image));
    };
    const reset = () => {
        if (!window.confirm("سيتم مسح جميع البيانات المدخلة (اسم الجهة، اللوحات، عرض السعر). هل تريد المتابعة؟"))
            return;
        releaseAll(data);
        setData(emptyProposal());
    };
    const locate = (anchor) => {
        setPreviewOnly(false);
        requestAnimationFrame(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    };

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && setPreviewOnly(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const saveLabel = saving ? "جارٍ الحفظ..." : uploads > 0 ? "جارٍ رفع الصور..." : dirty ? "حفظ" : "محفوظ ✓";

    return createPortal(<div className={`proposal-app ${previewOnly ? "is-preview-only" : ""}`} dir="rtl">
      <header className="topbar">
        <div className="topbar-title">
          <button type="button" className="btn ghost" onClick={back}>
            → عروض الأسعار
          </button>
          <strong>Artika</strong>
          <span>مولّد العروض</span>
        </div>
        <div className="topbar-actions">
          <button type="button" className="btn primary" onClick={save} disabled={saving || uploads > 0 || loading || Boolean(loadError) || (!dirty && Boolean(id))}>
            {saveLabel}
          </button>
          <button type="button" className="btn" onClick={() => setPreviewOnly((v) => !v)}>
            {previewOnly ? "العودة للمحرر" : "معاينة العرض"}
          </button>
          {!link && (<button type="button" className="btn ghost" onClick={reset} disabled={loading}>
              إعادة تعيين
            </button>)}
          <button type="button" className="btn" onClick={() => window.print()} disabled={loading}>
            طباعة / تصدير PDF
          </button>
        </div>
      </header>

      {loading || loadError ? (<div className="workspace proposal-status">
          {loadError ? `تعذّر تحميل العرض: ${loadError}` : "جارٍ تحميل العرض..."}
        </div>) : (<div className="workspace">
        <aside className="editor" aria-label="محرر العرض">
          {link?.invoiceLink && link.stage === "quote" && (<QuotePanel link={link} canManage={canManageProject} busy={stageBusy || saving || uploads > 0} onApprove={approveQuote} onEditQuote={() => leaveTo(`/invoice/edit/${link.invoiceLink.invoiceId}`)}/>)}
          {link?.invoiceLink && link.stage !== "quote" && (<InvoicePanel link={link} approving={approving} busy={saving || uploads > 0} onApprove={approve} onOpenInvoice={() => leaveTo(`/invoice/edit/${link.invoiceLink.invoiceId}`)}/>)}

          <section className="panel">
            <h2>
              <span className="step">1</span> الجهة المقدم إليها الاقتراح
              <small>Recipient</small>
            </h2>
            <label className="field">
              <span>اسم الجهة كما يظهر في صفحة الغلاف بعد «اقتراح الى:»</span>
              <input value={data.recipientName} maxLength={90} placeholder="مثال: مطعم الو بيروت" onChange={(e) => setData({ ...data, recipientName: e.target.value })}/>
            </label>
            <button type="button" className="link" onClick={() => locate("page-cover")}>
              عرض الغلاف ◎
            </button>
            {isAdmin && (<div className="admin-settings">
                <div className="admin-settings-title">🔧 إعدادات المدير - الغلاف</div>
                <BrandOffsetControl offset={Number(data.coverBrandOffset) || 0} min={COVER_BRAND_OFFSET_MIN} max={COVER_BRAND_OFFSET_MAX} onChange={(coverBrandOffset) => setData((d) => ({ ...d, coverBrandOffset }))}/>
              </div>)}
          </section>

          <section className="panel">
            <h2>
              <span className="step">2</span> المشروع وترتيب اللوحات
              <small>Project</small>
            </h2>
            <ProjectEditor project={data.project} trackUpload={trackUpload} onChange={(next) => setData((d) => ({ ...d, project: typeof next === "function" ? next(d.project) : next }))} onLocate={locate}/>
          </section>

          <section className="panel">
            <h2>
              <span className="step">3</span> اللوحات المقترحة
              <small>Proposed artworks</small>
            </h2>
            <p className="hint">
              ارفع الصور فقط — التصميم والإطارات والمقاسات ثابتة حسب القالب. في الإطارات الثابتة تُقص الصورة تلقائيًا دون تمطيط أو تشويه، ويمكن ضبط القص.
              الجداريات وصور التطبيق العمودية تُعرض كاملة بنسبتها الأصلية.
            </p>
            <ArtworksEditor pages={data.artworks} trackUpload={trackUpload} isAdmin={isAdmin} onChange={(next) => setData((d) => ({ ...d, artworks: typeof next === "function" ? next(d.artworks) : next }))} onLocate={(pageId) => locate(`page-${pageId}`)}/>
          </section>

          <section className="panel">
            <h2>
              <span className="step">4</span> عرض السعر
              <small>Price offer</small>
            </h2>
            {link ? (<LinkedPriceSummary offer={data.price} fromQuote={link.stage === "quote"}/>) : (<>
                <p className="hint">المجاميع تُحسب تلقائيًا. الشروط والأحكام ثابتة حسب القالب.</p>
                <PriceEditor offer={data.price} onChange={(price) => setData({ ...data, price })}/>
              </>)}
            <button type="button" className="link" onClick={() => locate("page-price")}>
              عرض صفحة السعر ◎
            </button>
          </section>

          <p className="fixed-note">
            الصفحات الثابتة (الغلاف، الهوية، المحتويات، من نحن، الرؤية والرسالة، ما الذي نقدمه، آلية العمل، الخاتمة، تواصل معنا) جزء من قالب أرتيكا ولا يمكن تعديلها.
          </p>
        </aside>

        <main className="preview" ref={previewRef} style={{ "--page-scale": scale }} aria-label="معاينة العرض">
          <ProposalDocument data={data} invoiceData={link?.invoiceData}/>
        </main>
      </div>)}
    </div>, document.body);
}
